import React, { useRef, useEffect } from "react";
import defaultProfile from "../UserProfile/EmptyProfile.jpg";
import "./Grade1/Grade1ScienceLesson.css"; 

/* 
  StoryModeQuiz and other UIs for Science, modeled after Math.
*/

// --- Story Mode Quick Quiz --- //
export function ScienceStoryModeQuiz({
    questions,
    onFinish,
    studentAvatar
  }) {
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [score, setScore] = React.useState(0);
    const [feedback, setFeedback] = React.useState(null); // NEW: For instant feedback
    const [locked, setLocked] = React.useState(false); // NEW: To prevent double clicks
  
    if (
      !questions ||
      !Array.isArray(questions) ||
      questions.length === 0 ||
      !questions[currentIndex] ||
      typeof questions[currentIndex].question !== "string"
    ) {
      return (
        <div style={{ color: "red", textAlign: "center", margin: "32px 0" }}>
          <b>Quiz Error: No valid questions available.</b>
        </div>
      );
    }
  
    const currentQ = questions[currentIndex];
  
    function handleAnswer(opt) {
      if (locked) return;
      setLocked(true);
    
      if (opt === currentQ.correctAnswer) {
        setFeedback("✅ Correct!");
        // Instead of relying on setScore's async update, use a variable:
        const nextScore = score + 1;
        setScore(nextScore);
    
        setTimeout(() => {
          setFeedback(null);
          setLocked(false);
    
          if (currentIndex < questions.length - 1) {
            setCurrentIndex(i => i + 1);
          } else {
            if (typeof onFinish === "function") onFinish(nextScore, questions.length);
          }
        }, 900);
      } else {
        setFeedback(
          `❌ Incorrect! The correct answer is: "${currentQ.correctAnswer}"`
        );
        setTimeout(() => {
          setFeedback(null);
          setLocked(false);
    
          if (currentIndex < questions.length - 1) {
            setCurrentIndex(i => i + 1);
          } else {
            if (typeof onFinish === "function") onFinish(score, questions.length);
          }
        }, 1500);
      }
    }
    
  
    return (
      <div className="g1sci-quiz-story-mode">
        <div className="g1sci-quiz-top-panel">
          <div className="g1sci-teacher-image-container">
            <img className="g1sci-quiz-character-img" src="/images/Teacher.png" alt="Teacher" />
          </div>
          <div className="g1sci-quiz-question-box">
            <p className="g1sci-quiz-question-text">{currentQ.question}</p>
          </div>
        </div>
        <div className="g1sci-quiz-bottom-panel">
          <div className="g1sci-quiz-answer-box-left">
            {currentQ.options.map((opt, i) => (
              <button
                key={i}
                className="g1sci-quiz-answer-btn"
                onClick={() => handleAnswer(opt)}
                disabled={!!feedback || locked}
              >
                {opt}
              </button>
            ))}
            <div className="g1sci-quiz-score-display">
              Question {currentIndex + 1} of {questions.length} | Current Score: {score}
            </div>
            {feedback && (
              <div className="g1sci-quiz-feedback" style={{ marginTop: 14, fontSize: 18 }}>
                {feedback}
              </div>
            )}
          </div>
          <div className="g1sci-student-image-container">
            <img className="g1sci-quiz-character-img-student" src={studentAvatar || defaultProfile} alt="Student" />
          </div>
        </div>
      </div>
    );
  }
  
  

// --- Quiz Result Screen --- //
export function ScienceQuizResultScreen({
  score,
  total,
  onContinue,
  onRetry,
  continueLabel = "Continue",
  retryLabel = "Retry"
}) {
  return (
    <div className="quiz-result-screen">
      <h3>Quiz Finished!</h3>
      <p>Your Score: {score} / {total}</p>
      <div className="quiz-result-actions">
        <button className="quiz-result-btn" onClick={onRetry}>
          {retryLabel}
        </button>
        <button className="quiz-result-btn" onClick={onContinue}>
          {continueLabel}
        </button>
      </div>
    </div>
  );
}

// --- Final Quiz UI --- //
export function ScienceFinalQuizUI({
    questions,
    currentIndex,
    score,
    timeLeft,
    onAnswer,
    quizFinished,
    onReturn,
    onTokenAwarded // Optional: callback to parent for awarding token
  }) {
    const hasAwarded = useRef(false);
  
    // Compute percentage and trigger award only once
    useEffect(() => {
      if (
        quizFinished &&
        questions.length > 0 &&
        !hasAwarded.current
      ) {
        const percent = Math.round((score / questions.length) * 100);
        if (percent >= 80 && typeof onTokenAwarded === "function") {
          onTokenAwarded();
          hasAwarded.current = true;
        }
      }
      // eslint-disable-next-line
    }, [quizFinished, questions, score, onTokenAwarded]);
  
    // Defensive: If questions array is broken
    if (
      !questions ||
      !Array.isArray(questions) ||
      questions.length === 0 ||
      !questions[currentIndex] ||
      typeof questions[currentIndex].question !== "string"
    ) {
      return (
        <div style={{ color: "red", textAlign: "center", margin: "32px 0" }}>
          <b>Quiz Error: No valid questions available for final quiz.</b>
        </div>
      );
    }
  
    const percent = Math.round((score / questions.length) * 100);
  
    if (quizFinished) {
      return (
        <div className="final-quiz-container">
          <div className="final-quiz-finished">
            <h3 className="final-quiz-finished-title">Final Quiz Completed!</h3>
            <p className="final-quiz-finished-score">
            Your Score: {score} / {questions.length} 
            ({questions.length > 0 ? Math.round((score / questions.length) * 100) : "N/A"}%)
            </p>
            {percent >= 80 ? (
              <div style={{ color: "#228B22", fontSize: 28, margin: "24px 0" }}>
                🎉 Congratulations! You earned a Science token!
              </div>
            ) : (
              <div style={{ color: "#d35400", fontSize: 24, margin: "24px 0" }}>
                Try again to earn a Science token (80% required).
              </div>
            )}
            <button className="final-quiz-finish-btn" onClick={onReturn}>
              Return to Lesson
            </button>
          </div>
        </div>
      );
    }
  
    // In-progress quiz UI:
    const q = questions[currentIndex];
    return (
      <div className="final-quiz-container">
        <h2 className="final-quiz-title">Science Final Quiz</h2>
        <p className="final-quiz-timer">
          Time Left: {Math.floor(timeLeft / 60)}:
          {String(timeLeft % 60).padStart(2, "0")}
        </p>
        <p className="final-quiz-progress">
          Question {currentIndex + 1} / {questions.length}
        </p>
        <p className="final-quiz-question">{q.question}</p>
        <div className="final-quiz-options">
          {q.options.map((choice, idx) => (
            <button
              key={idx}
              className="final-quiz-option"
              onClick={() => onAnswer(choice)}
            >
              {choice}
            </button>
          ))}
        </div>
        <p className="final-quiz-score">
          Current Score: {score}
        </p>
      </div>
    );
  }
